import {
  humanizeEntityRef,
  EntityRefLink,
  EntityRefLinks,
} from '@backstage/plugin-catalog-react';
import { Chip } from '@material-ui/core';
import { CatalogTableRow } from './types';
import { OverflowTooltip, TableColumn } from '@backstage/core-components';
import { Entity, parseEntityRef } from '@backstage/catalog-model';
import { JsonArray } from '@backstage/types';
import { GitHubLink, SlackUserLink } from '@appfolio/backstage-components';
import {
  DEFAULT_NAMESPACE,
  entityUtil,
} from '@appfolio/backstage-plugin-catalog-common';
import { includesInvariantCase } from '@appfolio/backstage-plugin-utils-common';
import { ANNOTATION_SLACK_ID } from '@appfolio/backstage-plugin-catalog-common';

export const columnFactories = Object.freeze({
  createNameColumn(options?: {
    defaultKind?: string;
  }): TableColumn<CatalogTableRow> {
    function formatContent(entity: Entity): string {
      return (
        entity.metadata?.title ??
        humanizeEntityRef(entity, {
          defaultKind: options?.defaultKind,
        })
      );
    }
    return {
      title: 'Name',
      field: 'resolved.name',
      highlight: true,
      customSort({ entity: entity1 }, { entity: entity2 }) {
        return formatContent(entity1).localeCompare(formatContent(entity2));
      },
      render: ({ entity }) => (
        <EntityRefLink
          entityRef={entity}
          defaultKind={options?.defaultKind ?? 'Component'}
          title={entity.metadata?.title}
        />
      ),
    };
  },
  createEntityName(): TableColumn<CatalogTableRow> {
    return {
      title: 'Entity Name',
      field: 'resolved.name',
      render: ({ resolved }) =>
        parseEntityRef(resolved.name, {
          defaultKind: 'component',
          defaultNamespace: DEFAULT_NAMESPACE,
        }).name,
    };
  },
  createPartOfColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Part Of',
      field: 'resolved.partOfRelationsTitle',
      render: ({ resolved }) => (
        <EntityRefLinks
          entityRefs={resolved.partOfRelations}
          defaultKind={resolved.partOfRelationsDefaultKind}
        />
      ),
    };
  },
  createMemberOfColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Member Of',
      field: 'resolved.memberOfRelationsTitle',
      render: ({ resolved }) => (
        <EntityRefLinks
          entityRefs={resolved.memberOfRelations}
          defaultKind="group"
        />
      ),
    };
  },
  createUserRoleColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Role',
      field: 'resolved.userRole',
    };
  },
  createUserTitleColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Title',
      field: 'resolved.userTitle',
    };
  },
  createUserLocationColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Location',
      field: 'resolved.userLocation',
    };
  },
  createGitHubUsernameColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'GitHub',
      field: 'resolved.userGitHubUsername',
      render: ({ resolved }) =>
        resolved.userGitHubUsername ? (
          <GitHubLink
            pathQuery={resolved.userGitHubUsername}
            text={resolved.userGitHubUsername}
          />
        ) : null,
    };
  },
  createOwnerColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Owner',
      field: 'resolved.ownedByRelationsTitle',
      render: ({ resolved }) => (
        <EntityRefLinks
          entityRefs={resolved.ownedByRelations}
          defaultKind="group"
        />
      ),
    };
  },
  createRepoColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Repo',
      field: 'resolved.repo',
      render: ({ resolved }) =>
        resolved.repo ? (
          <GitHubLink pathQuery={resolved.repo} text={resolved.repo} />
        ) : null,
      customSort({ resolved: a }, { resolved: b }) {
        return (a.repo ??= '').localeCompare(b.repo ?? '');
      },
      customFilterAndSearch: (query: string, row: CatalogTableRow) =>
        includesInvariantCase(row.resolved.repo ?? '', query),
    };
  },
  createSpecTargetsColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Targets',
      field: 'entity.spec.targets',
      render: ({ entity }) => (
        <>
          {(entity?.spec?.targets ?? entity?.spec?.target) && (
            <OverflowTooltip
              text={(
                (entity!.spec!.targets as JsonArray) ?? [entity.spec.target]
              ).join(', ')}
              placement="bottom-start"
            />
          )}
        </>
      ),
    };
  },
  createSpecTypeColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Type',
      field: 'entity.spec.type',
      hidden: true,
      width: 'auto',
    };
  },
  createSpecLifecycleColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Lifecycle',
      field: 'entity.spec.lifecycle',
    };
  },
  createMetadataDescriptionColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Description',
      field: 'entity.metadata.description',
      render: ({ entity }) => (
        <OverflowTooltip
          text={entity.metadata.description}
          placement="bottom-start"
        />
      ),
      width: 'auto',
    };
  },
  createTagsColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Tags',
      field: 'entity.metadata.tags',
      cellStyle: {
        padding: '0px 16px 0px 20px',
      },
      render: ({ entity }) => (
        <>
          {entity.metadata.tags &&
            entity.metadata.tags.map(t => (
              <Chip
                key={t}
                label={t}
                size="small"
                variant="outlined"
                style={{ marginBottom: '0px' }}
              />
            ))}
        </>
      ),
      width: 'auto',
    };
  },
  createTitleColumn(options?: {
    hidden?: boolean;
  }): TableColumn<CatalogTableRow> {
    return {
      title: 'Title',
      field: 'entity.metadata.title',
      hidden: options?.hidden,
      searchable: true,
    };
  },
  createLabelColumn(
    key: string,
    options?: { title?: string; defaultValue?: string },
  ): TableColumn<CatalogTableRow> {
    return {
      title: options?.title ?? 'Label',
      field: 'entity.metadata.labels',
      cellStyle: {
        padding: '0px 16px 0px 20px',
      },
      render: ({ entity }: { entity: Entity }) => {
        const labels: Record<string, string> | undefined =
          entity.metadata?.labels;
        const specifiedLabelValue =
          (labels && labels[key]) ?? options?.defaultValue;
        return (
          <>
            {specifiedLabelValue && (
              <Chip
                key={specifiedLabelValue}
                label={specifiedLabelValue}
                size="small"
                variant="outlined"
              />
            )}
          </>
        );
      },
      width: 'auto',
    };
  },
  createNamespaceColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Namespace',
      field: 'entity.metadata.namespace',
      width: 'auto',
    };
  },
  createSlackUsernameColumn(): TableColumn<CatalogTableRow> {
    return {
      title: 'Slack',
      field: 'resolved.slack',
      render: ({ resolved, entity }) => {
        const e = entityUtil(entity);
        const id = e.getAnnotation(ANNOTATION_SLACK_ID);
        return id && resolved.slack ? (
          <SlackUserLink id={id} name={resolved.slack} />
        ) : null;
      },
    };
  },
});
